<script setup lang="ts">
import TableHeading from '@/Components/Common/TableHeading.vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/16/solid';
import type { SortColumn, SortDirection } from './InvoiceTable.vue';

const props = defineProps<{
    sortColumn: SortColumn;
    sortDirection: SortDirection;
}>();

const emit = defineEmits<{
    sort: [column: SortColumn];
}>();

function handleSort(column: SortColumn) {
    emit('sort', column);
}

function isSorted(column: SortColumn): boolean {
    return props.sortColumn === column;
}

function isChevronDown(column: SortColumn): boolean {
    return isSorted(column) && props.sortDirection === 'desc';
}

function isChevronUp(column: SortColumn): boolean {
    return isSorted(column) && props.sortDirection === 'asc';
}
</script>

<template>
    <TableHeading>
        <div
            class="py-1.5 pr-3 text-left text-text-tertiary pl-4 sm:pl-6 lg:pl-8 3xl:pl-12 cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('reference')">
            Reference
            <ChevronDownIcon v-if="isChevronDown('reference')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('reference')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div
            class="px-3 py-1.5 text-left text-text-tertiary cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('recipient')">
            Recipient
            <ChevronDownIcon v-if="isChevronDown('recipient')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('recipient')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div
            class="px-3 py-1.5 text-left text-text-tertiary cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('status')">
            Status
            <ChevronDownIcon v-if="isChevronDown('status')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('status')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div
            class="px-3 py-1.5 text-left text-text-tertiary cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('date')">
            Date
            <ChevronDownIcon v-if="isChevronDown('date')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('date')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div
            class="px-3 py-1.5 text-left text-text-tertiary cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('due_at')">
            Due Date
            <ChevronDownIcon v-if="isChevronDown('due_at')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('due_at')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div
            class="px-3 py-1.5 text-left text-text-tertiary cursor-pointer hover:bg-secondary hover:text-text-primary transition-colors select-none flex items-center gap-1"
            @click="handleSort('paid_date')">
            Paid At
            <ChevronDownIcon v-if="isChevronDown('paid_date')" class="w-4 h-4" />
            <ChevronUpIcon v-else-if="isChevronUp('paid_date')" class="w-4 h-4" />
            <span v-else class="w-4 h-4"></span>
        </div>
        <div class="px-3 py-1.5 text-left font-semibold text-text-primary">
            <span class="sr-only">Download</span>
        </div>

        <div class="relative py-1.5 pl-3 pr-4 sm:pr-6 lg:pr-8 3xl:pr-12">
            <span class="sr-only">Actions</span>
        </div>
    </TableHeading>
</template>

<style scoped></style>
